import {type Line} from '../../../util/plotHelpers/types';

/**
 * Represents a series entry for uPlot data alignment.
 * - 'primary': A regular line series (non-aux)
 * - 'band-min': The min (y) values from an aux/area line
 * - 'band-max': The max (y0) values from an aux/area line
 */
export type SeriesEntry = {
  line: Line;
  type: 'primary' | 'band-min' | 'band-max';
};

type SeriesIndices = {
  bandMinSeriesIndex?: number;
  bandMaxSeriesIndex?: number;
  primarySeriesIndex?: number;
};

type LineMetricMap = Map<
  string, // metric name
  SeriesIndices
>;

export type LineNameMap = Map<
  string, // line name
  LineMetricMap
>;

// Build series entries: bands first (so they render behind), then primary lines
export function getSeriesEntries(lines: Line[]): {
  seriesEntries: (SeriesEntry | null)[];
  lineNameMap: LineNameMap;
} {
  // null entry is to align the array with uPlot's 1-indexed series
  const entries: (SeriesEntry | null)[] = [null];

  // Add band series first (min and max pairs) - they render behind primary lines
  const primaryLines = [];
  const auxLines = [];
  for (const line of lines) {
    if (line.aux) {
      auxLines.push(line);
    } else {
      primaryLines.push(line);
    }
  }

  const lineNameMap = new Map<string, LineMetricMap>();
  for (const line of auxLines) {
    if (line.name != null && line.metricName != null) {
      let lineMetricMap = lineNameMap.get(line.name);
      if (!lineMetricMap) {
        lineMetricMap = new Map<string, SeriesIndices>();
      }
      lineMetricMap.set(line.metricName, {
        bandMinSeriesIndex: entries.length,
        bandMaxSeriesIndex: entries.length + 1,
      });
      lineNameMap.set(line.name, lineMetricMap);
    }
    entries.push({type: 'band-min', line});
    entries.push({
      type: 'band-max',
      line: line,
    });
  }

  // Then add primary lines on top
  for (const line of primaryLines) {
    if (line.name != null) {
      if (line.name != null && line.metricName != null) {
        let lineMetricMap = lineNameMap.get(line.name);
        if (!lineMetricMap) {
          lineMetricMap = new Map<string, SeriesIndices>();
        }
        const lineNameMapEntry = lineMetricMap.get(line.metricName);

        if (lineMetricMap) {
          lineMetricMap.set(line.metricName, {
            ...lineNameMapEntry,
            primarySeriesIndex: entries.length,
          });
        }
        lineNameMap.set(line.name, lineMetricMap);
      }
    }
    entries.push({type: 'primary', line});
  }

  return {seriesEntries: entries, lineNameMap};
}
